import React from "react";
import { motion } from "framer-motion";
import { Clock, BadgeCheck, FileCheck2, Hourglass, RefreshCw, Award } from "lucide-react";

const rows = [
  { id: "Supplier A-104", participating: true, offer: true, lead: "18 d", inspection: "Confirmed", activity: "Offer Submitted", pulse: false },
  { id: "Supplier B-208", participating: true, offer: true, lead: "12 d", inspection: "Confirmed", activity: "Awarded", pulse: false, awarded: true },
  { id: "Supplier C-317", participating: true, offer: true, lead: "16 d", inspection: "Confirmed", activity: "Updated", pulse: true },
  { id: "Supplier D-442", participating: true, offer: true, lead: "14 d", inspection: "Confirmed", activity: "Qualified", pulse: false },
  { id: "Supplier E-551", participating: true, offer: true, lead: "15 d", inspection: "Confirmed", activity: "Awaiting Review", pulse: false },
  { id: "Supplier F-663", participating: true, offer: false, lead: "—", inspection: "—", activity: "Participating", pulse: false },
];

const activityStyles = {
  "Offer Submitted": "bg-emerald-50 text-emerald-700 border-emerald-200/70",
  Updated: "bg-[#3B82F6]/10 text-[#1D4ED8] border-[#3B82F6]/25",
  Qualified: "bg-[#0B1F3A]/[0.06] text-[#0B1F3A] border-[#0B1F3A]/20",
  "Awaiting Review": "bg-violet-50 text-violet-700 border-violet-200/70",
  Awarded: "bg-[#0B1F3A] text-white border-[#0B1F3A]",
  Participating: "bg-[#3B82F6]/10 text-[#1D4ED8] border-[#3B82F6]/25",
  Waiting: "bg-amber-50 text-amber-700 border-amber-200/70",
};

const commodityStrip = [
  { name: "Wheat Flour", image: "/commodities/wheat-flour.png", active: true },
  { name: "Sunflower Oil", image: "/commodities/sunflower-oil.png" },
  { name: "Spaghetti", image: "/commodities/spaghetti.png" },
  { name: "Bulgur", image: "/commodities/bulgur.png" },
  { name: "Semolina", image: "/commodities/semolina.png" },
];

export default function Intelligence() {
  return (
    <section
      data-testid="intelligence-section"
      id="procurement-activity"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="font-inter text-[11px] font-bold tracking-[0.22em] uppercase text-[#3B82F6]">
            Live Procurement Activity
          </p>
          <h2 className="mt-3 font-display text-3xl md:text-[42px] font-extrabold tracking-tight text-[#0B1F3A] leading-[1.1]">
            Real-time bidding activity.<br />
            <span className="text-[#475569]">Full process visibility.</span>
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#475569] leading-relaxed">
            Track anonymous participation and factory offers across food commodity import
            bidding — wheat flour, sunflower oil, pasta, bulgur and semolina from verified Turkish manufacturers.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="mt-12 rounded-[24px] bg-white border border-slate-200/80 cb-elevated-shadow overflow-hidden"
          data-testid="intelligence-table"
        >
          <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100 bg-[#FAFBFD]">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500 cb-pulse" />
              <span className="font-display font-semibold text-[14px] text-[#0B1F3A]">
                Food Commodity Import Bidding · Wheat Flour 100 MT · Kenya
              </span>
            </div>
            <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200/70 px-2 py-1 rounded-md">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 cb-pulse" />
              Live · 11h 42m remaining
            </span>
          </div>

          <div className="px-6 py-3 border-b border-slate-100 bg-white flex items-center gap-3 overflow-x-auto">
            <span className="text-[10px] font-bold tracking-wider uppercase text-[#64748B] shrink-0">
              Commodities
            </span>
            <div className="flex items-center gap-2">
              {commodityStrip.map((c) => (
                <div
                  key={c.name}
                  className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 shrink-0 ${
                    c.active
                      ? "border-[#3B82F6]/40 bg-[#3B82F6]/5"
                      : "border-slate-100 bg-[#F8FAFC]"
                  }`}
                >
                  <div className="h-7 w-7 rounded-md overflow-hidden border border-slate-100">
                    <img src={c.image} alt={c.name} className="h-full w-full object-cover" />
                  </div>
                  <span className="text-[11px] font-semibold text-[#0B1F3A] whitespace-nowrap">{c.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden md:grid grid-cols-[1.2fr_0.8fr_0.8fr_0.7fr_0.8fr_1fr] gap-4 px-6 py-3 border-b border-slate-100 text-[10.5px] font-bold uppercase tracking-wider text-[#64748B]">
            <span>Supplier</span>
            <span>Participating</span>
            <span>Offer Submitted</span>
            <span>Lead Time</span>
            <span>Inspection</span>
            <span className="text-right">Activity</span>
          </div>

          <div className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`relative grid grid-cols-1 md:grid-cols-[1.2fr_0.8fr_0.8fr_0.7fr_0.8fr_1fr] gap-3 md:gap-4 px-6 py-5 items-center transition-colors hover:bg-slate-50/70 ${
                  r.awarded ? "bg-[#0B1F3A]/[0.03]" : ""
                }`}
              >
                {r.awarded && (
                  <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#0B1F3A]" />
                )}
                {r.pulse && (
                  <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#3B82F6]" />
                )}
                <div className="min-w-0">
                  <p className="font-mono font-semibold text-[13px] text-[#0B1F3A] truncate">
                    {r.id}
                  </p>
                  <p className="text-[11px] text-[#64748B]">
                    {r.awarded ? "Winning manufacturer" : "Anonymous supplier"}
                  </p>
                </div>
                <div>
                  <p className="md:hidden text-[10px] uppercase tracking-wider text-[#64748B]">Participating</p>
                  {r.participating ? (
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      Yes
                    </span>
                  ) : (
                    <span className="text-[12px] text-[#94A3B8]">—</span>
                  )}
                </div>
                <div>
                  <p className="md:hidden text-[10px] uppercase tracking-wider text-[#64748B]">Offer Submitted</p>
                  {r.offer ? (
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700">
                      <FileCheck2 className="h-3.5 w-3.5" />
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[12px] font-medium text-[#94A3B8]">
                      <Hourglass className="h-3.5 w-3.5" />
                      Pending
                    </span>
                  )}
                </div>
                <div>
                  <p className="md:hidden text-[10px] uppercase tracking-wider text-[#64748B]">Lead Time</p>
                  <div className="flex items-center gap-1.5 text-[13px] text-[#0B1F3A] font-medium">
                    {r.lead !== "—" ? (
                      <>
                        <Clock className="h-3 w-3 text-[#64748B]" />
                        {r.lead}
                      </>
                    ) : (
                      <span className="text-[#94A3B8]">—</span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="md:hidden text-[10px] uppercase tracking-wider text-[#64748B]">Inspection</p>
                  {r.inspection !== "—" ? (
                    <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded-md bg-emerald-50 text-emerald-700">
                      <BadgeCheck className="h-3 w-3" />
                      {r.inspection}
                    </span>
                  ) : (
                    <span className="text-[12px] text-[#94A3B8]">—</span>
                  )}
                </div>
                <div className="md:flex md:justify-end">
                  <p className="md:hidden text-[10px] uppercase tracking-wider text-[#64748B]">Activity</p>
                  <span
                    className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-1 rounded-md border ${
                      activityStyles[r.activity] || activityStyles.Participating
                    }`}
                  >
                    {r.activity === "Updated" && <RefreshCw className="h-2.5 w-2.5" />}
                    {r.activity === "Awarded" && <Award className="h-2.5 w-2.5" />}
                    {r.pulse && r.activity === "Updated" && (
                      <span className="h-1.5 w-1.5 rounded-full bg-[#3B82F6] cb-pulse" />
                    )}
                    {r.activity}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="px-6 py-4 border-t border-slate-100 bg-[#FAFBFD] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-[12px] text-[#64748B]">
              Activity updates in real time as verified manufacturers submit competitive factory offers.
            </p>
            <p className="text-[12px] font-medium text-[#0B1F3A]">
              7 factory offers · 9 participating · 2 waiting
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
